import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import Button from 'components/Button/Button';
import ContentLoader from 'components/ContentLoader/ContentLoader';
import EmptyTaskManagement from 'components/EmptyTaskManagement/EmptyTaskManagement';
import Loader from 'components/Loader/Loader';
import Panel from 'components/Panel/Panel';
import Select from 'components/Select/Select';
import TitleTableWrapper from 'components/TitleTableWrapper/TitleTableWrapper';
import { TASK_HEAD_ANALYSIS_CELLS } from 'constants/dataSets';
import { OPTION_ANALYSIS_FILTER } from 'constants/general';
import { BUTTON_VARIANT } from 'constants/interfaces';
import { getTextDate } from 'utils/formattedData';

import ItemsTable from './components/ItemsTable/ItemsTable';
import UpdateItem from './components/updateItem/updateItem';

import type IMaterialityConfigAnalysis from './interfaces/IMaterialityConfigAnalysis';
import type IOptionSelect from './interfaces/IOptionSelect';

import styles from './scss/MaterialityConfigAnalysisView.module.scss';

function MaterialityConfigAnalysisView({
  goBack, onSave, isEmptyList, loading, items, onLoadMore, hasMore,
  onEditItem, onRefreshInfo, selectedOption, onSelectedOption,
  openModal, selectedItem, closeModal, onUpdateItem,
  loadingAI, lastUpdate, locationsCountries,
}: IMaterialityConfigAnalysis) {
  const { t } = useTranslation();

  const handleSelectChange = (newValue: unknown) => {
    if (newValue && typeof newValue === 'object' && 'value' in newValue && 'label' in newValue) {
      onSelectedOption(newValue as IOptionSelect);
    }
  };

  return (
    <Panel className={styles.root}>
      <Button
        title={t('materialityAssessment.back')}
        onClick={goBack}
        variant={BUTTON_VARIANT.ghost}
        startIcon={<ArrowLeftIcon />}
      />
      <div className={styles.rootContainer}>
        <div className={styles.wrapper}>
          <TitleTableWrapper
            title={t('materialityAssessment.stakeholderAnalysis')}
            hint={t('materialityAssessment.hintStakeholderAnalysis')}
          />
          <div className={styles.buttonsFilter}>
            <Select
              placeholder={t('materialityAssessment.placeholder.selectCategory')}
              options={OPTION_ANALYSIS_FILTER}
              value={selectedOption?.label ? selectedOption : undefined}
              onChange={handleSelectChange}
              className={styles.select}
              required
            />
            <div className={styles.wrapperUpdate}>
              {lastUpdate > 0 ? (
                <span className={styles.titleDate}>
                  <strong>{`${t('materialityAssessment.lastUpdated')}`}</strong>
                  {' '}
                  {getTextDate(new Date(lastUpdate))}
                </span>
              ) : null}
              <Button
                title={t('materialityAssessment.refreshInformation')}
                onClick={onRefreshInfo}
                className={styles.button}
                disabled={loadingAI}
              />
            </div>
          </div>
          <div className={styles.contentWrapper}>
            {loading ? <Loader isPageLoading={loading} isFullLoader={false} /> : null}
            {loadingAI ? (<ContentLoader title={t('materialityAssessment.loadingAnalysis')} />) : null}
            {isEmptyList && !loading && !loadingAI ? (
              <EmptyTaskManagement
                title={t('materialityAssessment.emptyAnalysisDescription')}
                checkIcon
              />
            ) : null}
            {!isEmptyList && !loading && !loadingAI ? (
              <div className={styles.tableWrapper}>
                {items && items.length > 0 ? (
                  <ItemsTable
                    items={items}
                    headCells={TASK_HEAD_ANALYSIS_CELLS}
                    onLoadMore={onLoadMore}
                    hasMore={hasMore}
                    isLoadingMore={loading}
                    onEditItem={onEditItem}
                    locationsCountries={locationsCountries}
                    isInfoTable
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={goBack}
          />
          <Button
            title={t('common.save')}
            onClick={onSave}
            className={styles.button}
            disabled={loadingAI}
          />
        </div>
        <UpdateItem
          open={openModal}
          onClose={closeModal}
          title={t('materialityAssessment.titleAnalysisEdit')}
          onUpdateItem={onUpdateItem}
          isPageLoading={loading}
          selectedItem={selectedItem}
          locationsCountries={locationsCountries}
        />
      </div>
    </Panel>
  );
}

export default MaterialityConfigAnalysisView;
